import axios from 'axios';
import i18n from '../../../i18n';
import type {
  ErrorCatalogResponse,
  BusinessThrowRequest,
  ValidationThrowRequest,
  AuthThrowRequest,
  ServerThrowRequest,
  ApiError,
} from '../types/errorDemo';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

/**
 * Dedicated axios instance for error-demo.
 * NOTE: The /simulate/auth endpoint intentionally returns 401 — we must NOT
 * trigger the global logout interceptor for this prefix.
 * attachAuthInterceptors bypasses all /simulate/* paths automatically.
 *
 * R8-E-001 (2026-05-05 enhance #255): URL path changed from /throw/* to
 * /simulate/* to bypass Nginx WAF that blocks the literal word "throw".
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
// attachAuthInterceptors is registered FIRST so it sees the raw HTTP error;
// /simulate/* is excluded from global-logout so 401 from auth simulation is
// passed through to the feature-level interceptor below.
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token')
  );
}

// Request interceptor — attach Bearer token + Accept-Language (R7-085)
// Accept-Language reflects current i18next language so BE resolves messages
// in the same locale as the FE.  When user toggles ko/en the query key in
// useErrorCodes changes which triggers a refetch with the updated header.
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  const lang = i18n.language ?? 'ko';
  config.headers['Accept-Language'] = lang === 'en' ? 'en' : 'ko';
  return config;
});

/**
 * Response interceptor — for /simulate/* endpoints, 4xx/5xx are EXPECTED responses.
 * We extract the ErrorResponse body and resolve (not reject) so mutations can
 * display the result card without triggering global error handlers.
 *
 * For GET /codes (the only success endpoint), we unwrap ApiResponse<T>.data.
 */
apiClient.interceptors.response.use(
  (response) => {
    // Successful 2xx — unwrap ApiResponse<T>
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      // success=false in 2xx body (unlikely) — treat as error
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: (err?.['code'] as string) ?? `HTTP-${response.status}`,
        message: (err?.['message'] as string) ?? 'Unknown error',
        detail: (err?.['detail'] as string) ?? undefined,
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
        statusCode: response.status,
      } satisfies ApiError);
    }
    return response;
  },
  (error) => {
    // 4xx / 5xx — extract ErrorResponse body
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const rawData = error.response?.data;
    const statusCode: number = error.response?.status ?? 0;
    const respCt = String(error.response?.headers?.['content-type'] ?? '');

    /* R11-#308: WAF/proxy 가 BE 의 4xx/5xx 응답을 HTML 400 으로 변조하는 경우 (R7-090 / R8-E-001 후속).
       응답 Content-Type 이 text/html 이거나 raw body 가 HTML 인 경우, axios 기본 메시지
       'Request failed with status code 400' 로 빠지지 않게 명시적으로 사용자에게 안내한다. */
    const looksLikeHtml = respCt.includes('text/html')
      || (typeof rawData === 'string' && rawData.trimStart().startsWith('<'));

    const code = looksLikeHtml
      ? 'NETWORK-WAF-001'
      : ((errData?.['code'] as string) ?? `HTTP-${statusCode || 'UNKNOWN'}`);
    const message = looksLikeHtml
      ? '응답이 JSON 이 아닙니다 — 토큰 만료 또는 WAF/프록시 변조 가능성. 페이지를 새로고침하고 재로그인 해주세요.'
      : ((errData?.['message'] as string)
          ?? (typeof rawData === 'object' && rawData !== null
                ? ((rawData as Record<string, unknown>).message as string)
                : undefined)
          ?? (statusCode > 0 ? `HTTP ${statusCode} — ${error.message ?? 'Unknown error'}` : 'Network error'));
    const detail = (errData?.['detail'] as string) ?? undefined;
    const traceId =
      (errData?.['traceId'] as string) ??
      (error.response?.headers?.['x-trace-id'] as string) ??
      undefined;
    const messageEn = (errData?.['messageEn'] as string) ?? undefined;
    const timestamp = (errData?.['timestamp'] as string) ?? undefined;
    const fieldErrors = (errData?.['fieldErrors'] as ApiError['fieldErrors']) ?? undefined;

    return Promise.reject({
      code,
      message,
      detail,
      traceId,
      statusCode,
      messageEn,
      timestamp,
      fieldErrors,
    } satisfies ApiError);
  }
);

export const errorDemoApi = {
  /**
   * GET /api/eraf/error-demo/codes
   * Returns the ErrorCode catalog (4 groups: COMMON / AUTH / VALIDATION / USER).
   */
  getCatalog: (): Promise<ErrorCatalogResponse> =>
    apiClient
      .get<unknown, { data: ErrorCatalogResponse }>('/error-demo/codes')
      .then((res) => res.data),

  /**
   * POST /api/eraf/error-demo/simulate/business
   * Always throws — returns 4xx (never 200).
   * R8-E-001: renamed from /throw/business to bypass WAF.
   */
  throwBusiness: (req: BusinessThrowRequest): Promise<never> =>
    apiClient.post('/error-demo/simulate/business', req) as Promise<never>,

  /**
   * POST /api/eraf/error-demo/simulate/validation
   * Throws 400 with fieldErrors when validation fails.
   * May return 200 {"ok": true} if input is valid (unlikely in demo usage).
   * R8-E-001: renamed from /throw/validation to bypass WAF.
   */
  throwValidation: (req: ValidationThrowRequest): Promise<{ ok: boolean }> =>
    apiClient
      .post<unknown, { data: { ok: boolean } }>('/error-demo/simulate/validation', req)
      .then((res) => res.data),

  /**
   * POST /api/eraf/error-demo/simulate/auth
   * Always throws 401 or 403 depending on variant.
   * NOTE: 401 here is intentional — the axios interceptor must NOT logout.
   * R8-E-001: renamed from /throw/auth to bypass WAF.
   */
  throwAuth: (req: AuthThrowRequest): Promise<never> =>
    apiClient.post('/error-demo/simulate/auth', req) as Promise<never>,

  /**
   * POST /api/eraf/error-demo/simulate/server
   * Always throws 500.
   * R8-E-001: renamed from /throw/server to bypass WAF.
   */
  throwServer: (req: ServerThrowRequest): Promise<never> =>
    apiClient.post('/error-demo/simulate/server', req) as Promise<never>,
};
